import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";

import { Bullet } from "src/components/Icons";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { useUserProfile } from "src/hooks/ui";

import { SLImage } from "../../components";
import { kRoute } from "../../kRoute";
import BrandDashboard from "./BrandDashboard";
import RepDashboard from "./RepDashboard";

const LegacyDashBoard: React.FC = () => {
  const navigation = useNavigation();
  const { isAdmin } = useUserProfile();
  const rFirstName = useSelector(({ UserReducer }) => UserReducer.profile.firstName);

  const _handleNavToViewOrders = () => {
    navigation.navigate(kRoute.P03);
  };

  const _handleNavToMessages = () => {
    navigation.navigate(kRoute.P04);
  };

  const _handleNavToConnections = () => {
    navigation.navigate(kRoute.P06);
  };

  const _handleNavToPlatformStatus = () => {
    if (!isAdmin) return;
    navigation.navigate(kRoute.P11);
  };

  const _handleNavToApplications = () => {
    if (!isAdmin) return;
    navigation.navigate(kRoute.P13);
  };

  return (
    <>
      <View style={[styles.darken, styles.absolute]} />
      <SLImage
        source={{ uri: "/images/bg_dashboard.jpg" }}
        mobileStyle={styles.P02_ImageBackground}
        webStyle={webStyles.P02_ImageBackground}
        resizeMode="cover"
      />
      <ScrollView style={styles.P02_ContentView}>
        <View style={styles.preventTextBreak}>
          <Text style={[styles.header, styles.mb4]}>{`Welcome, ${rFirstName}!`}</Text>
        </View>
        <View style={styles.P02_WrapTextAroundImage}>
          <Text style={[styles.subheader, styles.mb4]}>This is your Administrative View where you can:</Text>
          {isAdmin && (
            <>
              <TouchableOpacity style={[styles.button, styles.mb1]} onPress={_handleNavToPlatformStatus}>
                <Bullet color={kColors.white} style={[styles.buttonLeftIcon, styles.mr3]} />
                <Text style={styles.buttonLabel}>View the TMT Platform Status</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.button, styles.mb1]} onPress={_handleNavToApplications}>
                <Bullet color={kColors.white} style={[styles.buttonLeftIcon, styles.mr3]} />
                <Text style={styles.buttonLabel}>View pending applications</Text>
              </TouchableOpacity>
            </>
          )}
          <TouchableOpacity style={[styles.button, styles.mb1]} onPress={_handleNavToViewOrders}>
            <Bullet color={kColors.white} style={[styles.buttonLeftIcon, styles.mr3]} />
            <Text style={styles.buttonLabel}>View your orders</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.button, styles.mb1]} onPress={_handleNavToMessages}>
            <Bullet color={kColors.white} style={[styles.buttonLeftIcon, styles.mr3]} />
            <Text style={styles.buttonLabel}>View your messages</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.button} onPress={_handleNavToConnections}>
            <Bullet color={kColors.white} style={[styles.buttonLeftIcon, styles.mr3]} />
            <Text style={styles.buttonLabel}>Manage your customers</Text>
          </TouchableOpacity>
          <Text style={[styles.subheader, styles.mt4]}>and much more!</Text>
        </View>
      </ScrollView>
    </>
  );
};

const P02_Dashboard: React.FC = () => {
  const rIsLoading = useSelector((state) => state.LoadingReducer.userFetching);
  const { isBrand, isRep } = useUserProfile();

  if (rIsLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  if (isBrand) {
    return <BrandDashboard />;
  }

  if (isRep) {
    return <RepDashboard />;
  }

  return <LegacyDashBoard />;
};

export const P02 = React.memo(P02_Dashboard);

const styles = StyleSheet.create({
  P02_ContentView: {
    flex: 1,
    paddingHorizontal: kStyleConst.kDashboardHorizontalPadding,
    paddingVertical: kStyleConst.kDashboardVerticalPadding,
  },
  P02_ImageBackground: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: -1,
  },
  P02_WrapTextAroundImage: {
    width: "75%",
  },
  absolute: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
  },
  alignCenter: {
    alignItems: "center",
  },
  button: {
    alignItems: "center",
    flexDirection: "row",
  },
  buttonLabel: {
    ...kFonts.ralewaySmallRegular,
    color: kColors.white,
    lineHeight: 22,
    overflowWrap: "normal",
    textTransform: "uppercase",
  },
  buttonLeftIcon: {
    height: 7,
    width: 7,
  },
  darken: {
    backgroundColor: kColors.black,
    opacity: 0.25,
  },
  flex1: {
    flex: 1,
  },
  header: {
    ...kFonts.ralewayXXLargeMedium,
    color: kColors.white,
    letterSpacing: 2,
    overflowWrap: "normal",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb1: {
    marginBottom: kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mr3: {
    marginRight: 3 * kStyleConst.kSpacing,
  },
  mt4: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  preventTextBreak: {
    flexDirection: "row",
  },
  subheader: {
    ...kFonts.ralewayLargeLight,
    color: kColors.white,
    letterSpacing: 1,
    overflowWrap: "normal",
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  P02_ImageBackground: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: -1,
  },
  placeholderPortrait: {
    height: 80,
    width: 80,
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
  },
} as const;
